import React, { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useRef } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  TableOptions,
  PaginationState
} from '@tanstack/react-table'
import { CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight } from '@phosphor-icons/react'
import DropdownButton from '../form-elements/DropdownButton'
import { HardDrive } from 'react-feather'
import Loader from '../loader-components/Loader'
import TableFilterHeader from './TableFilterHeader'
import { FilterHeader, FilterHeaderWithIcon } from 'src/lib/types/genericTable'
import { classNames } from 'src/lib/utils/helpers'

interface GenericTableProps<T extends object> {
  data: T[]
  columns: ColumnDef<T>[]
  pagination?: {
    paginationState: PaginationState
    setPaginationState: Dispatch<SetStateAction<PaginationState>>
  }
  totalRows?: number
  tableOptions?: Partial<TableOptions<T>>
  loading?: boolean
  handleRowClick?: (rowData: T) => void
  isCustomizable: {
    shouldShowFilterHeader?: boolean
    filterHeader?: FilterHeader[]
    setFilterHeaders?: Dispatch<SetStateAction<FilterHeader[]>>
    filterHeaderWithIcon?: FilterHeaderWithIcon[]
    setFilterHeaderWithIcon?: Dispatch<SetStateAction<FilterHeaderWithIcon[]>>
    handleStatusSection?: (label: string, section: FilterHeader[]) => FilterHeader[]
    handleFilterClick?: (label: string, state: boolean) => void
    isDisabled?: boolean
    isFullWidth?: boolean
    isResizable?: boolean
    hideDropDown?: boolean
    hidePaginationButton?: boolean
    isPointerEnable?: boolean
    isHorizontalScrollEnabled?: boolean
    hideHeaders?: boolean
    applyBorder?: boolean
    removeBorders?: boolean
    cellClassName?: string
    headerClassName?: string
  }
  customPaginationConfig: {
    customPagination?: boolean
    customPageSizeOptionsForDropdown?: { value: number; label: string }[]
  }
  emptyStateText?: string
  emptyStateAction?: ReactNode
  tableHeaderComp?: {
    shouldShowHeader: boolean
    tableHeader: ReactNode
  }
  tableFooterComp?: {
    shouldShowTableFooter: boolean
    tableFooter: ReactNode
  }
}

const GenericTable = <T extends object>({
  data = [],
  columns,
  tableOptions = {},
  pagination,
  totalRows = 0,
  loading,
  handleRowClick,
  customPaginationConfig = {
    customPagination: false,
    customPageSizeOptionsForDropdown: [
      { value: 10, label: '10' },
      { value: 25, label: '25' },
      { value: 50, label: '50' }
    ]
  },
  isCustomizable = {
    shouldShowFilterHeader: false,
    filterHeader: [],
    setFilterHeaders: () => {},
    filterHeaderWithIcon: [],
    setFilterHeaderWithIcon: () => {},
    handleStatusSection: () => [],
    handleFilterClick: () => {},
    isDisabled: false,
    isFullWidth: false,
    isResizable: false,
    hideDropDown: false,
    hidePaginationButton: false,
    isPointerEnable: false,
    isHorizontalScrollEnabled: true,
    hideHeaders: false,
    applyBorder: false,
    removeBorders: false,
    cellClassName: '',
    headerClassName: ''
  },
  emptyStateText = 'No data available',
  emptyStateAction,
  tableHeaderComp = {
    shouldShowHeader: false,
    tableHeader: <></>
  },
  tableFooterComp = {
    shouldShowTableFooter: false,
    tableFooter: <></>
  }
}: GenericTableProps<T>) => {
  const { paginationState, setPaginationState } = pagination || {}

  const table = useReactTable({
    data,
    columns,
    enableColumnResizing: Boolean(isCustomizable.isResizable),
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    onPaginationChange: pagination?.setPaginationState,
    state: {
      pagination: pagination?.paginationState
    },
    manualPagination: Boolean(pagination),
    pageCount: pagination ? Math.ceil(totalRows / pagination.paginationState.pageSize) : 1,
    ...tableOptions
  })

  const defaultPageSizeOptionsForDropdown = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' }
  ]

  const pageSizeOptionsForDropdown =
    customPaginationConfig.customPageSizeOptionsForDropdown || defaultPageSizeOptionsForDropdown

  const previousDisabled = paginationState?.pageIndex ? paginationState.pageSize === 0 : true
  const nextDisabled = data.length < (paginationState?.pageSize ?? 0)

  const paginationButtons = useMemo(() => {
    const currentPage = table.getState().pagination?.pageIndex + 1
    const totalPages = table.getPageCount()
    const pageButtons = []
    let startPage = Math.max(currentPage - 1, 1)
    const endPage = Math.min(startPage + 3, totalPages)

    if (endPage - startPage < 3) {
      startPage = Math.max(endPage - 3, 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          className={`border rounded text-sm px-3 py-2 ${currentPage === i ? 'bg-gray-lighter' : ''}`}
          onClick={() => table.setPageIndex(i - 1)}
        >
          {i}
        </button>
      )
    }

    return pageButtons
  }, [table, totalRows, pagination])

  const scrollableContainerRef = useRef<HTMLDivElement>(null)

  const showsPageSizeDropdown = !Boolean(isCustomizable.hideDropDown) && Boolean(pagination)
  const showsPaginationButtons =
    !Boolean(isCustomizable.hidePaginationButton) &&
    (!Boolean(customPaginationConfig.customPagination) || Boolean(pagination))
  const showPaginationFooter = showsPageSizeDropdown || showsPaginationButtons

  return isCustomizable.hideHeaders && loading ? (
    <div className='flex justify-center items-center h-full'>
      <Loader />
    </div>
  ) : (
    <div
      className={`border-coal-lighter max-w-full overflow-hidden ${
        isCustomizable.removeBorders ? 'border-0 rounded-none' : 'border-1 rounded-lg'
      }`}
    >
      {tableHeaderComp?.shouldShowHeader && tableHeaderComp?.tableHeader}
      {isCustomizable.shouldShowFilterHeader && (
        <TableFilterHeader
          loading={Boolean(loading)}
          filterHeader={isCustomizable.filterHeader ?? []}
          setFilterHeaders={isCustomizable.setFilterHeaders ?? (() => {})}
          filterHeaderWithIcon={isCustomizable.filterHeaderWithIcon ?? []}
          setFilterHeaderWithIcon={isCustomizable.setFilterHeaderWithIcon ?? (() => {})}
          handleStatusSection={isCustomizable.handleStatusSection}
          handleFilterClick={isCustomizable.handleFilterClick}
          isDisabled={isCustomizable.isDisabled}
        />
      )}
      <div
        ref={scrollableContainerRef}
        className={`max-h-[700px] overflow-y-auto ${isCustomizable.removeBorders ? 'rounded-none' : 'rounded-lg'} ${
          isCustomizable.isHorizontalScrollEnabled ? 'overflow-x-auto' : 'overflow-x-hidden'
        }`}
      >
        <table className={`${isCustomizable.isFullWidth ? 'min-w-max' : 'w-full'} table-auto`}>
          {!isCustomizable.hideHeaders && (
            <thead className='bg-gray-lighter '>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={classNames(
                        'relative px-6 pt-3 pb-3 pl-3 text-overflow-ellipsis bg-gray-lighter text-left font-semibold text-coal-light',
                        isCustomizable.headerClassName ?? ''
                      )}
                      style={{
                        width: header.getSize(),
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                      }}
                    >
                      <div className='flex items-center text-sm font-medium'>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={`absolute top-0 h-full cursor-col-resize right-0 w-1 bg-gray-light rounded-l-lg hover:bg-gray-light ${
                              header.column.getIsResizing() ? 'bg-gray-dark' : ''
                            }`}
                          ></div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}

              {Boolean(loading) && (
                <tr>
                  <td colSpan={columns.length}>
                    <div className='h-[5px] w-full bg-primary-light'>
                      <div className='animate-progress left-right h-full w-full bg-primary-dark'></div>
                    </div>
                  </td>
                </tr>
              )}
            </thead>
          )}
          <tbody className='divide-y-1 border-b-1'>
            {data.length === 0 && !loading ? (
              <tr>
                <td colSpan={columns.length} className='bg-white text-center font-sm text-gray-light py-8 px-4'>
                  <div className='inline-flex flex-col items-center justify-center gap-2'>
                    <HardDrive width={80} height={80} strokeWidth={1} className='text-gray-light' />
                    <span className='text-coal-light'>{emptyStateText}</span>
                    {emptyStateAction}
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className={classNames(
                    'bg-white hover:bg-gray-lightest transition-colors duration-200',
                    isCustomizable.isPointerEnable ? 'cursor-pointer' : ''
                  )}
                  onClick={e => {
                    e.stopPropagation()
                    handleRowClick && handleRowClick(row.original)
                  }}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td
                      key={cell.id}
                      className={classNames(
                        `px-3 py-2 overflow-hidden text-overflow-ellipsis text-coal-light text-sm`,
                        isCustomizable.applyBorder
                          ? `border-1 border-gray-light ${cellIndex === 0 ? 'border-l-0' : ''}`
                          : '',
                        isCustomizable.cellClassName ?? ''
                      )}
                      style={{
                        width: cell.column.getSize()
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Rendered only when it has controls, otherwise it leaves an empty strip under the table. */}
      {showPaginationFooter && (
        <div
          className={`flex bg-white w-full justify-end rounded-b-lg ${
            !isCustomizable.hideDropDown || !isCustomizable.hidePaginationButton ? 'py-2 px-4' : ''
          }`}
        >
          <div className='flex items-center gap-2'>
            {!Boolean(isCustomizable.hideDropDown) && pagination && (
              <DropdownButton
                options={pageSizeOptionsForDropdown}
                handleClick={(index: number) => {
                  table.setPageSize(pageSizeOptionsForDropdown[index]?.value)
                }}
                variant='outlined'
                title={'Rows per page'}
                selectedOption={pageSizeOptionsForDropdown?.findIndex(
                  option => option.value === paginationState?.pageSize
                )}
                size='large'
              />
            )}
            {!Boolean(customPaginationConfig.customPagination) && !Boolean(isCustomizable.hidePaginationButton) && (
              <>
                <button
                  className={`border rounded 
              ${!table.getCanPreviousPage() ? 'opacity-40' : ''} p-2`}
                  disabled={!table.getCanPreviousPage()}
                  onClick={() => table.firstPage()}
                >
                  <CaretDoubleLeft className='w-4 h-4' />
                </button>
                {paginationButtons}
                <button
                  className={`border rounded 
              ${!table.getCanNextPage() ? 'opacity-40' : ''} p-2`}
                  onClick={() => table.lastPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <CaretDoubleRight className='w-4 h-4' />
                </button>
              </>
            )}
            {Boolean(customPaginationConfig.customPagination) &&
              !Boolean(isCustomizable.hidePaginationButton) &&
              pagination && (
                <div className='flex flex-row gap-2 border-coal-lighter text-coal-dark'>
                  <button
                    className={classNames('border rounded', previousDisabled ? 'opacity-40' : '', 'px-2')}
                    disabled={previousDisabled}
                    onClick={() =>
                      pagination.setPaginationState({
                        ...pagination.paginationState,
                        pageIndex: pagination.paginationState.pageIndex - 1
                      })
                    }
                  >
                    <CaretLeft className='w-3 h-3' />
                  </button>
                  <div className='border rounded px-3 py-1.5'>{pagination.paginationState.pageIndex + 1}</div>
                  <button
                    className={classNames('border rounded', nextDisabled ? 'opacity-40' : '', 'px-2')}
                    disabled={nextDisabled}
                    onClick={() =>
                      pagination.setPaginationState({
                        ...pagination.paginationState,
                        pageIndex: pagination.paginationState.pageIndex + 1
                      })
                    }
                  >
                    <CaretRight className='w-3 h-3' />
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
      {tableFooterComp?.shouldShowTableFooter && tableFooterComp?.tableFooter}
    </div>
  )
}

export default GenericTable
